import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, Palette, Activity, Gauge, Wand2, CheckCircle2,
  AlertTriangle, Info, Lock, LockOpen, ShieldCheck, Zap, Sun, Camera, Sliders, Gamepad2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TABS = [
  { id: "amazing", label: "Make It Amazing", icon: Sparkles },
  { id: "lighting", label: "Lighting", icon: Sun },
  { id: "camera", label: "Camera", icon: Camera },
  { id: "post", label: "Post & Polish", icon: Sliders },
  { id: "hud", label: "Interface", icon: Gamepad2 },
  { id: "health", label: "Visual Health", icon: Activity },
  { id: "quality", label: "Quality Target", icon: Gauge },
  { id: "art", label: "Art Director", icon: Palette },
];

// Client-side scope → disciplines (mirrors backend AMAZING_SCOPES).
const SCOPE_SETS = {
  visuals: new Set(["visuals", "lighting", "materials", "vfx", "environment", "colour", "post", "ui"]),
  gameplay: new Set(["gameplay", "pacing", "controls", "combat", "feedback"]),
  animation: new Set(["animation", "movement", "transitions"]),
  audio: new Set(["audio", "sound", "music"]),
};
const SCOPE_BTNS = [
  { id: "everything", label: "Improve Everything", emoji: "🌟" },
  { id: "visuals", label: "Visuals Only", emoji: "🎨" },
  { id: "gameplay", label: "Gameplay Only", emoji: "🎮" },
  { id: "animation", label: "Animation Only", emoji: "🕺" },
  { id: "performance", label: "Performance Safe", emoji: "⚡" },
  { id: "customise", label: "Customise", emoji: "🎛️" },
];
const DISC_COLOR = (d) => {
  if (SCOPE_SETS.visuals.has(d)) return "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10";
  if (SCOPE_SETS.gameplay.has(d)) return "text-sky-300 border-sky-500/40 bg-sky-500/10";
  if (SCOPE_SETS.animation.has(d)) return "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
  if (SCOPE_SETS.audio.has(d)) return "text-amber-300 border-amber-500/40 bg-amber-500/10";
  return "text-white/60 border-white/15 bg-white/5";
};

export default function VisualDirector() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("amazing");
  const [meta, setMeta] = useState(null);        // {quality_target, dna}
  const [targets, setTargets] = useState(null);  // static metadata

  useEffect(() => {
    api.get("/visual/quality-targets").then((r) => setTargets(r.data)).catch(() => {});
    refreshMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  const refreshMeta = useCallback(() => {
    api.get(`/visual/${gameId}`).then((r) => setMeta(r.data)).catch(() => {});
  }, [gameId]);

  return (
    <div className="min-h-screen bg-[#070912] text-white" data-testid="visual-director">
      <div className="max-w-4xl w-full mx-auto p-5 lg:p-6">
        <div className="flex items-center gap-3 mb-1.5">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="visual-back" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-amber-400 text-black"><Palette className="w-5 h-5" /></span>
            <div>
              <h1 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight leading-none">Visual Director</h1>
              <p className="text-[11px] text-white/40 mt-0.5">Make everything look, move & feel amazing — intentional, cohesive, premium.</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 my-4" data-testid="visual-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`visual-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${tab === t.id ? "bg-white text-black border-white" : "bg-white/5 text-white/70 border-white/12 hover:bg-white/10"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "amazing" && <AmazingTab gameId={gameId} targets={targets} navigate={navigate} />}
        {tab === "lighting" && <LightingTab gameId={gameId} targets={targets} />}
        {tab === "camera" && <CameraTab gameId={gameId} targets={targets} meta={meta} onChange={refreshMeta} />}
        {tab === "post" && <PostTab gameId={gameId} targets={targets} meta={meta} onChange={refreshMeta} />}
        {tab === "hud" && <HudTab gameId={gameId} targets={targets} meta={meta} onChange={refreshMeta} />}
        {tab === "health" && <HealthTab gameId={gameId} />}
        {tab === "quality" && <QualityTab gameId={gameId} targets={targets} meta={meta} onSet={refreshMeta} />}
        {tab === "art" && <ArtTab gameId={gameId} meta={meta} onChange={refreshMeta} />}
      </div>
    </div>
  );
}

function Section({ children }) {
  return <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 lg:p-5">{children}</div>;
}

// ── Make It Amazing ─────────────────────────────────────────────────────────
function AmazingTab({ gameId, targets, navigate }) {
  const [target, setTarget] = useState("game");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);      // {overview, improvements, target_label}
  const [sel, setSel] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);      // {version, applied}

  const analyse = async () => {
    setBusy(true); setPlan(null); setSel(new Set()); setDone(null);
    try {
      const { data } = await api.post(`/visual/${gameId}/amazing/plan`, { target, focus: "everything" }, { timeout: 150000 });
      setPlan(data);
      setSel(new Set((data.improvements || []).map((i) => i.id)));  // default: everything selected
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draft a plan.");
    } finally { setBusy(false); }
  };

  const applyScope = (scope) => {
    if (!plan) return;
    if (scope === "customise") { setSel(new Set()); return; }
    const imps = plan.improvements || [];
    let ids;
    if (scope === "everything") ids = imps.map((i) => i.id);
    else if (scope === "performance") ids = imps.filter((i) => !i.heavy).map((i) => i.id);
    else { const s = SCOPE_SETS[scope] || new Set(); ids = imps.filter((i) => s.has(i.discipline)).map((i) => i.id); }
    setSel(new Set(ids));
  };

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const apply = async () => {
    const chosen = (plan?.improvements || []).filter((i) => sel.has(i.id));
    if (!chosen.length) { toast.error("Select at least one improvement."); return; }
    setApplying(true);
    try {
      const { data } = await api.post(`/visual/${gameId}/amazing/apply`, {
        instructions: chosen.map((i) => i.instruction),
        summary: `Make It Amazing — ${plan.target_label || "game"} (${chosen.length})`,
      }, { timeout: 150000 });
      if (data.changed) { setDone({ version: data.version, applied: data.applied }); toast.success(`Applied ${data.applied} improvement${data.applied === 1 ? "" : "s"} — saved as v${data.version} (undoable).`); }
      else toast.message(data.summary || "No safe change could be applied.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply improvements.");
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-4" data-testid="visual-amazing">
      <Section>
        <p className="text-sm text-white/70 mb-3">Point the AI at part of your game and it proposes a prioritised improvement plan across visuals, lighting, animation, sound & feel. Applied changes are <span className="text-white/90 font-semibold">snapshotted &amp; fully undoable</span>.</p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-white/50">Make amazing:</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="amazing-target"
            className="rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-white outline-none">
            {(targets?.amazing_targets || [{ id: "game", label: "Whole game" }]).map((t) => (
              <option key={t.id} value={t.id} className="bg-[#0b0f1c]">{t.label}</option>
            ))}
          </select>
          <Button onClick={analyse} disabled={busy} data-testid="amazing-analyse-btn"
            className="rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-400 hover:opacity-90 text-black font-bold">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : <><Sparkles className="w-4 h-4" /> Make it amazing</>}
          </Button>
        </div>
      </Section>

      {busy && (
        <div className="flex items-center gap-2 text-white/50 text-sm" data-testid="amazing-thinking">
          <Loader2 className="w-4 h-4 animate-spin text-fuchsia-400" /> Directing a multi-discipline visual pass…
        </div>
      )}

      {plan && (
        <Section>
          <div className="flex items-start gap-2 mb-3">
            <Wand2 className="w-4 h-4 text-fuchsia-300 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-white/90 font-semibold" data-testid="amazing-count">{(plan.improvements || []).length} improvements found</p>
              <p className="text-xs text-white/50">{plan.overview}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="amazing-scopes">
            {SCOPE_BTNS.map((s) => (
              <button key={s.id} onClick={() => applyScope(s.id)} data-testid={`amazing-scope-${s.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-white/70 hover:bg-white/10">
                <span>{s.emoji}</span> {s.label}
              </button>
            ))}
          </div>

          <div className="space-y-1.5" data-testid="amazing-list">
            {(plan.improvements || []).map((imp) => (
              <label key={imp.id} data-testid={`amazing-item-${imp.id}`}
                className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${sel.has(imp.id) ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06]" : "border-white/8 bg-black/20 hover:bg-white/[0.03]"}`}>
                <input type="checkbox" checked={sel.has(imp.id)} onChange={() => toggle(imp.id)} data-testid={`amazing-check-${imp.id}`}
                  className="mt-1 accent-fuchsia-500 w-4 h-4" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-white/90">{imp.title}</span>
                    <span className={`text-[9px] uppercase tracking-wide rounded-full px-1.5 py-0.5 border ${DISC_COLOR(imp.discipline)}`}>{imp.discipline}</span>
                    {imp.heavy && <span className="text-[9px] rounded-full px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300 inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />heavy</span>}
                  </div>
                  <p className="text-[11px] text-white/45 mt-0.5">{imp.rationale}</p>
                </div>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Button onClick={apply} disabled={applying || sel.size === 0} data-testid="amazing-apply-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
              {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <><CheckCircle2 className="w-4 h-4" /> Apply selected ({sel.size})</>}
            </Button>
            {done && (
              <span className="text-xs text-emerald-300 inline-flex items-center gap-1" data-testid="amazing-done">
                <CheckCircle2 className="w-3.5 h-3.5" /> Saved as v{done.version}
                <button onClick={() => navigate(`/app/build/${gameId}`)} className="underline hover:text-white ml-1" data-testid="amazing-open-builder">Open builder</button>
              </span>
            )}
          </div>
          <p className="text-[10px] text-white/30 mt-2">Every applied change is a new version in history — revert any time in the builder.</p>
        </Section>
      )}
    </div>
  );
}

// ── Visual Health ─────────────────────────────────────────────────────────
function HealthTab({ gameId }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);   // {issues, summary}
  const [fixing, setFixing] = useState(null);

  const run = async () => {
    setBusy(true); setRes(null);
    try { const { data } = await api.post(`/visual/${gameId}/health`, {}, { timeout: 120000 }); setRes(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run analysis."); }
    finally { setBusy(false); }
  };

  const fix = async (issue, idx) => {
    setFixing(idx);
    try {
      const { data } = await api.post(`/visual/${gameId}/amazing/apply`, {
        instructions: [issue.fix], summary: `Visual fix — ${issue.title}`,
      }, { timeout: 150000 });
      if (data.changed) { toast.success(`Fixed & saved as v${data.version} (undoable).`); setRes((r) => ({ ...r, issues: r.issues.filter((_, i) => i !== idx) })); }
      else toast.message(data.summary || "No safe change could be applied.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply the fix."); }
    finally { setFixing(null); }
  };

  return (
    <div className="space-y-4" data-testid="visual-health">
      <Section>
        <p className="text-sm text-white/70 mb-3">Scans your scene for <span className="text-white/90 font-semibold">real, measurable</span> presentation issues — contrast, empty space, flat lighting, palette overload, missing finishing. No arbitrary scores; every warning cites its evidence.</p>
        <Button onClick={run} disabled={busy} data-testid="health-run-btn" className="rounded-full bg-white text-black font-bold hover:bg-white/90">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</> : <><Activity className="w-4 h-4" /> Run visual health check</>}
        </Button>
      </Section>

      {res && (
        <Section>
          <p className="text-sm font-semibold text-white/90 mb-3" data-testid="health-summary">{res.summary}</p>
          {res.issues.length === 0 ? (
            <p className="text-sm text-emerald-300 inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> No clear visual issues — looking sharp.</p>
          ) : (
            <div className="space-y-2" data-testid="health-list">
              {res.issues.map((it, i) => (
                <div key={i} data-testid={`health-issue-${i}`} className="rounded-xl border border-white/8 bg-black/20 p-3">
                  <div className="flex items-start gap-2">
                    {it.severity === "warn"
                      ? <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      : <Info className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold text-white/90">{it.title}</span>
                        <span className="text-[9px] uppercase tracking-wide rounded-full px-1.5 py-0.5 border border-white/15 bg-white/5 text-white/50">{it.area}</span>
                        {it.ai && <span className="text-[9px] rounded-full px-1.5 py-0.5 border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300">AI</span>}
                      </div>
                      <p className="text-xs text-white/60 mt-1">{it.detail}</p>
                      {it.evidence && <p className="text-[11px] text-white/35 font-mono mt-1 break-words">{it.evidence}</p>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => fix(it, i)} disabled={fixing === i} data-testid={`health-fix-${i}`}
                      className="rounded-full shrink-0 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                      {fixing === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wand2 className="w-3.5 h-3.5" /> Fix</>}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

// ── Quality Target ──────────────────────────────────────────────────────────
function QualityTab({ gameId, targets, meta, onSet }) {
  const [saving, setSaving] = useState(null);
  const current = meta?.quality_target || "";
  const list = targets?.quality_targets || [];

  const set = async (id) => {
    setSaving(id);
    try { const { data } = await api.post(`/visual/${gameId}/quality-target`, { target: id }); toast.success(`Quality Target → ${data.quality_target} (renders at ${data.tier}). Saved v${data.version}.`); onSet(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set target."); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-4" data-testid="visual-quality">
      <Section>
        <p className="text-sm text-white/70">Set the visual bar for this game. GameView adapts fidelity — lighting, shadows, reflections, effects, post-processing — to hit your target on the player's device.</p>
      </Section>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="quality-grid">
        {list.map((q) => (
          <button key={q.id} onClick={() => set(q.id)} disabled={saving} data-testid={`quality-${q.id}`}
            className={`text-left rounded-2xl border p-4 transition-all ${current === q.id ? "border-amber-400/70 bg-amber-400/[0.08] ring-1 ring-amber-400/40" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
            <div className="flex items-center gap-2">
              <span className="text-xl">{q.emoji}</span>
              <span className="text-base font-bold text-white">{q.label}</span>
              {current === q.id && <span className="ml-auto text-[10px] rounded-full px-2 py-0.5 bg-amber-400 text-black font-bold" data-testid="quality-current">Current</span>}
              {saving === q.id && <Loader2 className="w-4 h-4 animate-spin text-amber-300 ml-auto" />}
            </div>
            <p className="text-xs text-white/70 mt-1.5">{q.desc}</p>
            <p className="text-[11px] text-white/40 mt-1">{q.detail}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Art Director ────────────────────────────────────────────────────────────
function ArtTab({ gameId, meta, onChange }) {
  const dna = meta?.dna;
  const hero = meta?.hero || {};
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [locking, setLocking] = useState(false);
  const [heroLocking, setHeroLocking] = useState(false);
  const [applying, setApplying] = useState(false);

  const toggleHeroLock = async () => {
    setHeroLocking(true);
    try {
      const { data } = await api.post(`/visual/${gameId}/hero/lock`, { locked: !hero.lock });
      toast.success(data.locked ? "🔒 Hero locked — AI edits & regenerations keep this look." : "Hero unlocked — appearance can change again.");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update the hero lock."); }
    finally { setHeroLocking(false); }
  };

  const check = async () => {
    if (!subject.trim()) return;
    setBusy(true); setReview(null);
    try { const { data } = await api.post(`/visual/${gameId}/art-director`, { subject: subject.trim() }, { timeout: 120000 }); setReview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't review that."); }
    finally { setBusy(false); }
  };

  const toggleLock = async () => {
    if (!dna) return;
    setLocking(true);
    try {
      await api.post(`/visual/${gameId}/dna`, {
        artStyle: dna.artStyle, palette: dna.palette, lightingStyle: dna.lightingStyle, mood: dna.mood, locked: !dna.locked,
      });
      toast.success(!dna.locked ? "Visual DNA locked — AI keeps new content on-style." : "Visual DNA unlocked.");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update DNA."); }
    finally { setLocking(false); }
  };

  const matchStyle = async () => {
    if (!review?.match_instruction) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/visual/${gameId}/amazing/apply`, {
        instructions: [review.match_instruction], summary: "Art Director — match game style",
      }, { timeout: 150000 });
      if (data.changed) { toast.success(`Brought on-style & saved as v${data.version} (undoable).`); setReview(null); setSubject(""); }
      else toast.message(data.summary || "No safe change could be applied.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply."); }
    finally { setApplying(false); }
  };

  return (
    <div className="space-y-4" data-testid="visual-art">
      <Section>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-emerald-300" />
          <p className="text-sm font-semibold text-white/90">Hero Consistency Lock</p>
          {hero.lock && <span className="text-[10px] rounded-full px-2 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">locked</span>}
          <Button size="sm" variant="outline" onClick={toggleHeroLock} disabled={heroLocking} data-testid="hero-lock-btn"
            className={`ml-auto rounded-full ${hero.lock ? "border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10" : "border-white/15 text-white/70 hover:bg-white/10"}`}>
            {heroLocking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : hero.lock ? <><Lock className="w-3.5 h-3.5" /> Locked</> : <><LockOpen className="w-3.5 h-3.5" /> Lock hero</>}
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl border border-white/15 grid place-items-center overflow-hidden shrink-0"
            style={{ background: hero.color || "#1a2036" }} data-testid="hero-preview">
            {hero.image ? <img src={hero.image} alt="hero" className="w-full h-full object-cover" />
              : <span className="text-3xl">{hero.emoji || "🦸"}</span>}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-white/70">{hero.model_url ? "3D model hero" : hero.image ? "Illustrated hero" : "Emoji hero"} · <span className="capitalize">{hero.kind || "hero"}</span></p>
            <div className="flex items-center gap-1.5 mt-1">
              {hero.color && <span className="w-5 h-5 rounded-md border border-white/15" style={{ background: hero.color }} title={hero.color} />}
              {hero.accent && <span className="w-5 h-5 rounded-md border border-white/15" style={{ background: hero.accent }} title={hero.accent} />}
              <span className="text-[11px] text-white/45 ml-1">{hero.lock ? "Face, outfit, colours & model are pinned across every AI edit." : "Lock to keep this hero identical everywhere."}</span>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <div className="flex items-center gap-2 mb-3">
          <Palette className="w-4 h-4 text-fuchsia-300" />
          <p className="text-sm font-semibold text-white/90">Your game's Visual DNA</p>
          {dna?.source === "creator" && <span className="text-[10px] rounded-full px-2 py-0.5 border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300">set by you</span>}
          <Button size="sm" variant="outline" onClick={toggleLock} disabled={locking || !dna} data-testid="dna-lock-btn"
            className={`ml-auto rounded-full ${dna?.locked ? "border-amber-400/50 text-amber-300 hover:bg-amber-400/10" : "border-white/15 text-white/70 hover:bg-white/10"}`}>
            {locking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : dna?.locked ? <><Lock className="w-3.5 h-3.5" /> Locked</> : <><LockOpen className="w-3.5 h-3.5" /> Lock DNA</>}
          </Button>
        </div>
        {dna ? (
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Palette</p>
              <div className="flex gap-1.5 flex-wrap" data-testid="dna-palette">
                {(dna.palette || []).map((c, i) => (
                  <span key={i} title={c} className="w-7 h-7 rounded-lg border border-white/15" style={{ background: c }} />
                ))}
                {(dna.palette || []).length === 0 && <span className="text-white/40 text-xs">—</span>}
              </div>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs text-white/70"><span className="text-white/40">Art style: </span>{dna.artStyle || "—"}</p>
              <p className="text-xs text-white/70"><span className="text-white/40">Lighting: </span>{dna.lightingStyle || "—"}</p>
              <p className="text-xs text-white/70"><span className="text-white/40">Mood: </span>{dna.mood || "—"}</p>
            </div>
          </div>
        ) : <p className="text-sm text-white/40">Loading DNA…</p>}
      </Section>

      <Section>
        <p className="text-sm text-white/70 mb-2">Describe something you want to add — the Art Director checks it against your world's style.</p>
        <div className="flex items-end gap-2">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="art-subject-input"
            onKeyDown={(e) => { if (e.key === "Enter") check(); }}
            placeholder="e.g. a photoreal concrete bunker with rusty metal"
            className="flex-1 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none" />
          <Button onClick={check} disabled={busy || !subject.trim()} data-testid="art-check-btn" className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Palette className="w-4 h-4" /> Check style</>}
          </Button>
        </div>

        {review && (
          <div className={`mt-3 rounded-xl border p-3 ${review.consistent ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-amber-500/40 bg-amber-500/[0.06]"}`} data-testid="art-review">
            <p className="text-sm text-white/90 flex items-start gap-2">
              {review.consistent ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />}
              <span data-testid="art-verdict">{review.verdict}</span>
            </p>
            {(review.issues || []).length > 0 && (
              <ul className="mt-2 ml-6 list-disc text-xs text-white/60 space-y-0.5">
                {review.issues.map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            )}
            {!review.consistent && review.match_instruction && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" onClick={matchStyle} disabled={applying} data-testid="art-match-btn" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold">
                  {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Wand2 className="w-3.5 h-3.5" /> Match Game Style</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setReview(null); setSubject(""); }} data-testid="art-keep-btn" className="rounded-full border-white/15 text-white/70 hover:bg-white/10">Keep Different</Button>
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Lighting Director ───────────────────────────────────────────────────────
function LightingTab({ gameId, targets }) {
  const presets = targets?.lighting_presets || [];
  const [saving, setSaving] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);   // {description}

  const applyPreset = async (id) => {
    setSaving(id);
    try { const { data } = await api.post(`/visual/${gameId}/lighting/preset`, { preset: id }); toast.success(`Lighting → ${data.label}. Saved v${data.version} (undoable).`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set lighting."); }
    finally { setSaving(null); }
  };

  const runPrompt = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setLast(null);
    try {
      const { data } = await api.post(`/visual/${gameId}/lighting/prompt`, { prompt: prompt.trim() }, { timeout: 120000 });
      setLast(data); toast.success(`Lighting set & saved v${data.version} (undoable).`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set that lighting."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="visual-lighting">
      <Section>
        <p className="text-sm text-white/70 mb-2">Describe the mood — the Lighting Director sets time of day, colour, fog, exposure & atmosphere. Every change is a new, undoable version.</p>
        <div className="flex items-end gap-2">
          <input value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="lighting-prompt-input"
            onKeyDown={(e) => { if (e.key === "Enter") runPrompt(); }}
            placeholder="e.g. make the boss room terrifying / a beautiful sunrise"
            className="flex-1 rounded-lg bg-black/40 border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none" />
          <Button onClick={runPrompt} disabled={busy || !prompt.trim()} data-testid="lighting-prompt-btn" className="rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sun className="w-4 h-4" /> Set lighting</>}
          </Button>
        </div>
        {last && <p className="text-xs text-emerald-300 mt-2 inline-flex items-center gap-1" data-testid="lighting-result"><CheckCircle2 className="w-3.5 h-3.5" /> {last.description}</p>}
      </Section>
      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Cinematic presets</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="lighting-presets">
          {presets.map((p) => (
            <button key={p.id} onClick={() => applyPreset(p.id)} disabled={saving} data-testid={`lighting-preset-${p.id}`}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left hover:bg-white/[0.07] transition-colors disabled:opacity-50">
              <span className="text-lg">{p.emoji}</span>
              <span className="text-sm font-semibold text-white/90 flex-1 truncate">{p.label}</span>
              {saving === p.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />}
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── Camera Director ─────────────────────────────────────────────────────────
function CameraTab({ gameId, targets, meta, onChange }) {
  const presets = targets?.camera_presets || [];
  const cam = meta?.camera || {};
  const [saving, setSaving] = useState(null);
  const [fov, setFov] = useState(cam.fov || 60);
  const [dist, setDist] = useState(cam.distance || 11);
  const [applying, setApplying] = useState(false);

  useEffect(() => { setFov((meta?.camera?.fov) || 60); setDist((meta?.camera?.distance) || 11); }, [meta]);

  const applyPreset = async (id) => {
    setSaving(id);
    try { const { data } = await api.post(`/visual/${gameId}/camera/preset`, { preset: id }); toast.success(`Camera → ${data.label}. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set camera."); }
    finally { setSaving(null); }
  };

  const applyManual = async () => {
    setApplying(true);
    try { const { data } = await api.post(`/visual/${gameId}/camera/set`, { fov: Number(fov), distance: Number(dist) }); toast.success(`Camera updated. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update camera."); }
    finally { setApplying(false); }
  };

  return (
    <div className="space-y-4" data-testid="visual-camera">
      <Section>
        <p className="text-sm text-white/70 mb-3">Tune how the third-person camera feels — a wider field of view adds a sense of speed, pulling back makes bosses & vistas feel huge. <span className="text-white/50">(3D games)</span></p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="camera-presets">
          {presets.map((p) => (
            <button key={p.id} onClick={() => applyPreset(p.id)} disabled={saving} data-testid={`camera-preset-${p.id}`}
              className={`text-left rounded-xl border p-3 transition-colors ${cam.style === p.id ? "border-sky-400/60 bg-sky-400/[0.08]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"} disabled:opacity-50`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{p.emoji}</span>
                <span className="text-sm font-bold text-white/90">{p.label}</span>
                {cam.style === p.id && <span className="ml-auto text-[10px] rounded-full px-2 py-0.5 bg-sky-400 text-black font-bold">Current</span>}
                {saving === p.id && <Loader2 className="w-4 h-4 animate-spin text-sky-300 ml-auto" />}
              </div>
              <p className="text-[11px] text-white/50 mt-1">{p.desc}</p>
            </button>
          ))}
        </div>
      </Section>
      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-3">Fine tune</p>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs text-white/70 mb-1"><span>Field of view</span><span data-testid="camera-fov-val" className="font-mono text-white/90">{fov}°</span></div>
            <input type="range" min="35" max="95" value={fov} onChange={(e) => setFov(e.target.value)} data-testid="camera-fov-slider" className="w-full accent-sky-400" />
            <div className="flex justify-between text-[10px] text-white/30"><span>Tight / filmic</span><span>Wide / fast</span></div>
          </div>
          <div>
            <div className="flex justify-between text-xs text-white/70 mb-1"><span>Follow distance</span><span data-testid="camera-dist-val" className="font-mono text-white/90">{dist}</span></div>
            <input type="range" min="4" max="40" step="1" value={dist} onChange={(e) => setDist(e.target.value)} data-testid="camera-dist-slider" className="w-full accent-sky-400" />
            <div className="flex justify-between text-[10px] text-white/30"><span>Close</span><span>Pulled back</span></div>
          </div>
          <Button onClick={applyManual} disabled={applying} data-testid="camera-apply-btn" className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold">
            {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <><Camera className="w-4 h-4" /> Apply camera</>}
          </Button>
        </div>
      </Section>
    </div>
  );
}

// ── Post-Processing & Colour Grading + Smart Clutter ─────────────────────────
function PostTab({ gameId, targets, meta, onChange }) {
  const posts = targets?.post_presets || [];
  const clutters = targets?.clutter_levels || [];
  const wfx = meta?.world_fx || {};
  const [savingPost, setSavingPost] = useState(null);
  const [savingClutter, setSavingClutter] = useState(null);
  const [exposure, setExposure] = useState(wfx.exposure || 1);
  const [bloom, setBloom] = useState(wfx.bloom != null ? wfx.bloom : 0.5);
  const [applying, setApplying] = useState(false);

  useEffect(() => { setExposure((meta?.world_fx?.exposure) || 1); setBloom((meta?.world_fx?.bloom != null) ? meta.world_fx.bloom : 0.5); }, [meta]);

  const applyPost = async (id) => {
    setSavingPost(id);
    try { const { data } = await api.post(`/visual/${gameId}/post/preset`, { preset: id }); toast.success(`Post FX → ${data.label}. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set post FX."); }
    finally { setSavingPost(null); }
  };
  const applySliders = async () => {
    setApplying(true);
    try { const { data } = await api.post(`/visual/${gameId}/post/set`, { exposure: Number(exposure), bloom: Number(bloom) }); toast.success(`Post FX updated. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update post FX."); }
    finally { setApplying(false); }
  };
  const applyClutter = async (id) => {
    setSavingClutter(id);
    try { const { data } = await api.post(`/visual/${gameId}/polish/clutter`, { level: id }); toast.success(`Scene density → ${data.label}. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set density."); }
    finally { setSavingClutter(null); }
  };

  return (
    <div className="space-y-4" data-testid="visual-post">
      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Colour grade & finishing looks</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="post-presets">
          {posts.map((p) => (
            <button key={p.id} onClick={() => applyPost(p.id)} disabled={savingPost} data-testid={`post-preset-${p.id}`}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 hover:bg-white/[0.07] transition-colors disabled:opacity-50">
              <span className="text-lg">{p.emoji}</span>
              <span className="text-sm font-semibold text-white/90 flex-1 truncate">{p.label}</span>
              {savingPost === p.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/60" />}
            </button>
          ))}
        </div>
      </Section>
      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-3">Fine tune</p>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-xs text-white/70 mb-1"><span>Exposure</span><span data-testid="post-exposure-val" className="font-mono text-white/90">{Number(exposure).toFixed(2)}×</span></div>
            <input type="range" min="0.6" max="1.5" step="0.02" value={exposure} onChange={(e) => setExposure(e.target.value)} data-testid="post-exposure-slider" className="w-full accent-fuchsia-400" />
          </div>
          <div>
            <div className="flex justify-between text-xs text-white/70 mb-1"><span>Bloom / glow</span><span data-testid="post-bloom-val" className="font-mono text-white/90">{Number(bloom).toFixed(2)}</span></div>
            <input type="range" min="0" max="1.5" step="0.05" value={bloom} onChange={(e) => setBloom(e.target.value)} data-testid="post-bloom-slider" className="w-full accent-fuchsia-400" />
          </div>
          <Button onClick={applySliders} disabled={applying} data-testid="post-apply-btn" className="rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold">
            {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <><Sliders className="w-4 h-4" /> Apply post FX</>}
          </Button>
        </div>
      </Section>
      <Section>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Smart Clutter — world density</p>
        <p className="text-xs text-white/50 mb-3">How much environment detail dresses the world <span className="text-white/40">(3D worlds; keeps performance in mind)</span>.</p>
        <div className="grid grid-cols-3 gap-2" data-testid="clutter-levels">
          {clutters.map((c) => (
            <button key={c.id} onClick={() => applyClutter(c.id)} disabled={savingClutter} data-testid={`clutter-${c.id}`}
              className="text-left rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-2 hover:bg-white/[0.07] transition-colors disabled:opacity-50">
              <div className="flex items-center gap-1.5"><span>{c.emoji}</span><span className="text-xs font-bold text-white/90">{c.label}</span>{savingClutter === c.id && <Loader2 className="w-3 h-3 animate-spin text-white/60 ml-auto" />}</div>
              <p className="text-[10px] text-white/40 mt-0.5">{c.desc}</p>
            </button>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ── UI / HUD Director (Interface) ────────────────────────────────────────────
function HudTab({ gameId, targets, meta, onChange }) {
  const themes = targets?.hud_themes || [];
  const cur = meta?.hud || {};
  const [saving, setSaving] = useState(null);
  const [accent, setAccent] = useState(cur.accent || "");

  useEffect(() => { setAccent((meta?.hud?.accent) || ""); }, [meta]);

  const apply = async (id) => {
    setSaving(id);
    try { const { data } = await api.post(`/visual/${gameId}/hud/preset`, { theme: id, accent: accent || "" }); toast.success(`HUD → ${data.label}. Saved v${data.version} (undoable).`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't set HUD."); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-4" data-testid="visual-hud">
      <Section>
        <p className="text-sm text-white/70 mb-1">Style the in-game heads-up display — score, health & prompts. The HUD follows the look you pick. <span className="text-white/50">(3D games)</span></p>
        <div className="flex items-center gap-2 mt-3 mb-1">
          <span className="text-xs text-white/60">Accent colour</span>
          <input type="color" value={accent || "#ffffff"} onChange={(e) => setAccent(e.target.value)} data-testid="hud-accent-input"
            className="w-8 h-8 rounded-md bg-transparent border border-white/15 cursor-pointer" />
          {accent && <button onClick={() => setAccent("")} className="text-[11px] text-white/40 hover:text-white/70 underline">reset</button>}
          <span className="text-[11px] text-white/40">applied when you pick a theme</span>
        </div>
      </Section>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="hud-themes">
        {themes.map((h) => (
          <button key={h.id} onClick={() => apply(h.id)} disabled={saving} data-testid={`hud-theme-${h.id}`}
            className={`text-left rounded-2xl border p-4 transition-all ${cur.theme === h.id ? "border-cyan-400/60 bg-cyan-400/[0.08] ring-1 ring-cyan-400/30" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"} disabled:opacity-50`}>
            <div className="flex items-center gap-2">
              <span className="text-xl">{h.emoji}</span>
              <span className="text-base font-bold text-white">{h.label}</span>
              <span className="ml-auto w-4 h-4 rounded-full border border-white/20" style={{ background: (cur.theme === h.id && accent) ? accent : h.accent }} />
              {cur.theme === h.id && <span className="text-[10px] rounded-full px-2 py-0.5 bg-cyan-400 text-black font-bold" data-testid="hud-current">Current</span>}
              {saving === h.id && <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />}
            </div>
            <p className="text-xs text-white/60 mt-1.5">{h.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
